import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { InboundEmailConfigsApiService, MessageCategoryInterface, TrackingMode, Visibility } from '@vendasta/email';
import { MessageCategory, MessageCategoryConfig } from './models';

@Injectable({
  providedIn: 'root',
})
export class EmailLabelingConfigService {
  private readonly inboundEmailConfigsApi = inject(InboundEmailConfigsApiService);

  listConfigs(namespace: string): Observable<MessageCategoryConfig[]> {
    return this.inboundEmailConfigsApi.listInboundEmailConfigs({ namespace }).pipe(
      map((response) => {
        if (!response.configurations) {
          return [];
        }
        return response.configurations
          .filter((config) => config.namespace && config.configurationId && config.name)
          .map((config) => ({
            namespace: config.namespace ?? '',
            configurationId: config.configurationId ?? '',
            name: config.name ?? '',
            categories: this.mapToMessageCategories(config.categories || []),
            trackingMode: config.trackingMode ?? TrackingMode.TRACKING_MODE_CONTACTS_ONLY,
            defaultInboundVisibility: config.defaultInboundVisibility ?? Visibility.VISIBILITY_PRIVATE,
            created: config.created ? new Date(config.created) : undefined,
            updated: config.updated ? new Date(config.updated) : undefined,
          }));
      }),
    );
  }

  getConfigById(namespace: string, configId: string): Observable<MessageCategoryConfig | null> {
    return this.inboundEmailConfigsApi.getMultiInboundEmailConfigs({ namespace, configurationIds: [configId] }).pipe(
      map((response) => {
        const config = response.configurations?.[0];
        if (!config || !config.namespace || !config.configurationId || !config.name) {
          return null;
        }
        return {
          namespace: config.namespace ?? '',
          configurationId: config.configurationId ?? '',
          name: config.name ?? '',
          categories: this.mapToMessageCategories(config.categories || []),
          trackingMode: config.trackingMode ?? TrackingMode.TRACKING_MODE_CONTACTS_ONLY,
          defaultInboundVisibility: config.defaultInboundVisibility ?? Visibility.VISIBILITY_PRIVATE,
          created: config.created ? new Date(config.created) : undefined,
          updated: config.updated ? new Date(config.updated) : undefined,
        };
      }),
    );
  }

  getAppliedConfig(namespace: string, userId: string): Observable<string | null> {
    return this.inboundEmailConfigsApi.getAppliedInboundEmailConfig({ namespace, userId }).pipe(
      map((response) => {
        return response.appliedConfiguration?.configurationId || null;
      }),
    );
  }

  createConfig(
    namespace: string,
    name: string,
    categories: MessageCategory[],
    trackingMode?: number,
    defaultInboundVisibility?: number,
  ): Observable<string> {
    return this.inboundEmailConfigsApi
      .createInboundEmailConfig({
        namespace,
        name,
        categories: this.mapToMessageCategoryInterfaces(categories),
        trackingMode: trackingMode ?? TrackingMode.TRACKING_MODE_CONTACTS_ONLY,
        defaultInboundVisibility: defaultInboundVisibility ?? Visibility.VISIBILITY_PRIVATE,
      })
      .pipe(
        map((response) => {
          return response.configurationId ?? '';
        }),
      );
  }

  updateConfig(
    namespace: string,
    configId: string,
    name: string,
    categories: MessageCategory[],
    trackingMode?: number,
    defaultInboundVisibility?: number,
  ): Observable<void> {
    return this.inboundEmailConfigsApi
      .updateInboundEmailConfig({
        namespace,
        configuration: {
          namespace,
          configurationId: configId,
          name,
          categories: this.mapToMessageCategoryInterfaces(categories),
          trackingMode: trackingMode ?? TrackingMode.TRACKING_MODE_CONTACTS_ONLY,
          defaultInboundVisibility: defaultInboundVisibility ?? Visibility.VISIBILITY_PRIVATE,
        },
      })
      .pipe(map(() => undefined));
  }

  deleteConfig(namespace: string, configId: string): Observable<void> {
    return this.inboundEmailConfigsApi
      .deleteInboundEmailConfig({
        namespace,
        configurationId: configId,
      })
      .pipe(map(() => undefined));
  }

  applyConfigToUser(namespace: string, userId: string, configId: string): Observable<void> {
    return this.inboundEmailConfigsApi
      .applyInboundEmailConfig({
        namespace,
        userId,
        configurationId: configId,
      })
      .pipe(map(() => undefined));
  }

  clearAppliedConfig(namespace: string, userId: string): Observable<void> {
    return this.inboundEmailConfigsApi.applyInboundEmailConfig({ namespace, userId }).pipe(map(() => undefined));
  }

  private mapToMessageCategories(categories: MessageCategoryInterface[]): MessageCategory[] {
    return categories
      .filter((cat) => cat.name && cat.color && cat.position !== undefined && cat.position !== null)
      .map((cat) => ({
        name: cat.name ?? '',
        color: cat.color ?? '#000000',
        position: cat.position ?? 0,
        description: cat.description,
        gmailLabelId: cat.gmailLabelId,
        outlookCategory: cat.outlookCategory,
      }));
  }

  private mapToMessageCategoryInterfaces(categories: MessageCategory[]): MessageCategoryInterface[] {
    return categories.map((cat) => ({
      name: cat.name,
      color: cat.color,
      position: cat.position,
      description: cat.description || '',
      gmailLabelId: cat.gmailLabelId,
      outlookCategory: cat.outlookCategory,
    }));
  }
}
